import apiService from './apiService';

interface UploadResponse {
  success: boolean;
  message: string;
  file: {
    filename: string;
    originalName: string;
    mimetype: string;
    size: number;
    url: string;
    path: string;
  };
}

interface FileListResponse {
  success: boolean;
  files: Array<{
    filename: string;
    size: number;
    createdAt: string;
    url: string;
  }>;
  total: number;
}

class MediaService {
  /**
   * رفع ملف (صورة/فيديو/مستند)
   */
  async uploadFile(file: File): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiService.post<UploadResponse>('/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response;
  }

  /**
   * حذف ملف
   */
  async deleteFile(filename: string): Promise<{ success: boolean; message: string }> {
    const response = await apiService.delete<{ success: boolean; message: string }>(
      `/media/delete/${filename}`
    );
    return response;
  }

  /**
   * الحصول على قائمة الملفات المرفوعة
   */
  async getFileList(): Promise<FileListResponse> {
    const response = await apiService.get<FileListResponse>('/media/list');
    return response;
  }

  /**
   * التحقق من نوع الملف
   */
  isValidFileType(file: File): boolean {
    const validTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'video/mp4',
      'video/3gpp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];

    return validTypes.includes(file.type);
  }

  /**
   * التحقق من حجم الملف
   */
  isValidFileSize(file: File, maxSizeMB: number = 16): boolean {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
  }

  /**
   * الحصول على نوع الملف (صورة/فيديو/مستند)
   */
  getFileType(file: File): 'image' | 'video' | 'document' | 'unknown' {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (
      file.type === 'application/pdf' ||
      file.type.includes('word') ||
      file.type.includes('excel')
    ) {
      return 'document';
    }
    return 'unknown';
  }

  /**
   * تنسيق حجم الملف
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
}

export default new MediaService();